import { useState } from "react";
import { BellRing, Mail, AlarmClock } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const REMINDER_OPTIONS = [
  { value: "0.5", label: "30 minit sebelum" },
  { value: "1", label: "1 jam sebelum" },
  { value: "2", label: "2 jam sebelum" },
  { value: "3", label: "3 jam sebelum" },
  { value: "6", label: "6 jam sebelum" },
];

const ROWS = [
  { key: "notify_on_approval", title: "Kelulusan & Penolakan Tempahan", desc: "Maklumkan staf apabila tempahan diluluskan atau ditolak." },
  { key: "notify_weekly_summary", title: "Ringkasan Mingguan", desc: "Emel ringkasan penggunaan fleet setiap Isnin pagi kepada pentadbir." },
];

export default function NotificationSettings({ tenant, reload }) {
  const s = tenant.settings || {};
  const [form, setForm] = useState({
    notify_email_enabled: s.notify_email_enabled ?? true,
    notify_on_approval: s.notify_on_approval ?? true,
    notify_weekly_summary: s.notify_weekly_summary ?? false,
    notify_trip_reminder: s.notify_trip_reminder ?? true,
    trip_reminder_hours: s.trip_reminder_hours ?? 1,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", form);
      toast.success("Tetapan notifikasi disimpan");
      reload();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-base flex items-center gap-2"><Mail className="h-4 w-4" /> Emel</CardTitle>
          <CardDescription>Emel dihantar melalui perkhidmatan Resend.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between border rounded-lg p-3">
            <div>
              <p className="text-sm font-semibold">Aktifkan Notifikasi Emel</p>
              <p className="text-xs text-muted-foreground">Matikan untuk menghentikan semua emel keluar tanpa menjejaskan notifikasi dalam sistem.</p>
            </div>
            <Switch checked={form.notify_email_enabled} onCheckedChange={(v) => setForm({ ...form, notify_email_enabled: v })} data-testid="settings-notify-email-toggle" />
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-base flex items-center gap-2"><AlarmClock className="h-4 w-4" /> Peringatan Perjalanan</CardTitle>
          <CardDescription>Emel automatik kepada staf sebelum perjalanan bermula dengan butang Mula/Tamat Perjalanan.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between border rounded-lg p-3">
            <div>
              <p className="text-sm font-semibold">Aktifkan Peringatan Perjalanan</p>
              <p className="text-xs text-muted-foreground">Staf menerima emel & notifikasi sebelum tempahan diluluskan bermula.</p>
            </div>
            <Switch checked={form.notify_trip_reminder} onCheckedChange={(v) => setForm({ ...form, notify_trip_reminder: v })} data-testid="settings-notify-trip-reminder" />
          </div>
          <div className="flex items-center justify-between border rounded-lg p-3">
            <div>
              <p className="text-sm font-semibold">Masa Peringatan</p>
              <p className="text-xs text-muted-foreground">Berapa awal peringatan dihantar sebelum masa mula perjalanan.</p>
            </div>
            <Select value={String(form.trip_reminder_hours)} onValueChange={(v) => setForm({ ...form, trip_reminder_hours: Number(v) })}>
              <SelectTrigger className="w-44" data-testid="settings-trip-reminder-hours"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {REMINDER_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-base flex items-center gap-2"><BellRing className="h-4 w-4" /> Jenis Pemberitahuan</CardTitle>
          <CardDescription>Notifikasi dalam sistem (loceng) sentiasa aktif; tetapan di bawah mengawal emel.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {ROWS.map((r) => (
            <div key={r.key} className="flex items-center justify-between border rounded-lg p-3">
              <div>
                <p className="text-sm font-semibold">{r.title}</p>
                <p className="text-xs text-muted-foreground">{r.desc}</p>
              </div>
              <Switch checked={form[r.key]} onCheckedChange={(v) => setForm({ ...form, [r.key]: v })} data-testid={`settings-${r.key.replaceAll("_", "-")}`} />
            </div>
          ))}
        </CardContent>
      </Card>

      <Button onClick={save} disabled={saving} className="bg-[#10056E] hover:bg-[#2a17a8]" data-testid="settings-save-btn">
        {saving ? "Menyimpan..." : "Simpan Tetapan"}
      </Button>
    </div>
  );
}
